import { get, compact } from "lodash"
import { createSelector } from "reselect"
import { Creators as ResourceCreators } from "modules/resources/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { Creators as DialogCreators } from "modules/dialog/model"
import { PERMISSION_LEVELS, hasPermission } from "helpers/permissions"
import { getAccount, getRawTopic, getPortfolioInfo } from "."
import { getFavorites } from "selectors/favorites"

import Confirm from "components/confirm-dialog"
import EditTopic from "components/edit-topic"
import saveRecentResources from "hocs/saveRecentResources"

const EMPTY_ITEMS = []

// Tolerates an absent topic: required before `chromeGate` can be adopted here (APP-3570).
const getIsFavorite = createSelector(
  [getRawTopic, getFavorites],
  (topic, favorites) => (topic ? get(favorites, topic.id, false) : false),
)

export const getTopicWithMeta = createSelector(
  [getRawTopic, getPortfolioInfo, getIsFavorite],
  // Null, not `{}`: a truthy empty object defeats consumers' `if (topic)` guards.
  (topic, portfolio, isFavorite) => (topic ? {
    ...topic,
    isFavorite,
    portfolioName: get(portfolio, "name"),
  } : null),
)

export const getContextualMenuItems = createSelector(
  [getAccount, getTopicWithMeta, getRawTopic],
  (account, topicWithMeta, topic) => (topic ? getTopicMenuItems(account, topicWithMeta) : EMPTY_ITEMS),
)
export function getTopicMenuItems(account, topic) {
  const { toggleFavorite, updateTopic, deleteTopic } = ResourceCreators
  const { open: openDialog, close: closeDialog } = DialogCreators
  const { add: addNotification } = NotificationCreators
  const { id, name, isFavorite, permissionLevel } = topic

  const editable = hasPermission(permissionLevel, PERMISSION_LEVELS.WRITE)
  const deletable = hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER)

  const EditTopicWithRecentResources = saveRecentResources(EditTopic, "id")

  return compact([
    editable && {
      id: "edit",
      label: "Edit",
      action: (dispatch) => dispatch(openDialog({
        // [TODO] [#184018795] JSX shouldn't be in selectors or models
        content: <EditTopicWithRecentResources dispatch={dispatch} account={account} topic={topic} {...topic} saveTopic={(...args) => dispatch(updateTopic(...args))} />,
      })),
    },
    {
      id: "toggleFavorite",
      label: isFavorite ? "Remove from favorites" : "Add to favorites",
      action: (dispatch) => {
        dispatch(toggleFavorite(id, "topic", !isFavorite))
        dispatch(addNotification("info", null, `${name} successfully ${isFavorite ? "removed from" : "added to"} favorites`))
      },
    },
    {
      id: "permissions",
      label: "View permissions",
      to: `/${account}/t/${id}/permissions/members`,
    },
    deletable && {
      id: "delete",
      label: "Delete",
      className: "danger",
      action: (dispatch, navigate) => {
        dispatch(openDialog({
          title: `Delete topic: ${name}`,
          content: <Confirm
            text={`Are you sure you want to delete ${name}? This will remove securities from this topic and permanently delete it.`}
            keyword={name}
            cta="Delete topic"
            action={() => {
              dispatch(deleteTopic(id))
              navigate(`/${account}/home/resources`)
            }}
            onClose={() => dispatch(closeDialog())}
          />,
        }))
      },
    },
  ])
}
